import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { API_BASE_URL } from "../config";
import "../styles/UserDashboard.css";
import "../styles/NewUserDashboard.css";

import checkboxIcon from "../ui-assets/home/checkbox.svg";
import circlerightIcon from "../ui-assets/home/circle_right_alt.svg";
import micIcon from "../ui-assets/home/mic_fill.svg";
import ticketIcon from "../ui-assets/home/ticket_fill.svg";
import createPlusIcon from "../ui-assets/home/create-event-plus.svg";
import editIcon from "../ui-assets/home/edit.svg";
import deleteIcon from "../ui-assets/home/delete_bin.svg";
import shareIcon from "../ui-assets/home/share.svg";
import dateIcon from "../ui-assets/home/event-back-date-icon.svg";
import locationIcon from "../ui-assets/home/location.svg";
import ProfilePage from "../components/ProfilePage";
import { CalendarDays, FileText, Mic, Users } from "lucide-react";



const UserDashboard = () => {
  // Authentication state
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [currentUser, setCurrentUser] = useState(
    JSON.parse(localStorage.getItem("user")),
  );
  const navigate = useNavigate();

  // Active section state
  const [activeSection, setActiveSection] = useState("dashboard");

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace("#", "");

      if (hash === "settings") {
        setActiveSection("settings");
      } else if (
        ["dashboard", "my-events", "attending", "speaking", "notifications"].includes(hash)
      ) {
        setActiveSection(hash);
      } else {
        setActiveSection("dashboard");
      }
    };

    // Check the hash when Dashboard first loads
    handleHashChange();

    // Check again whenever the hash changes
    window.addEventListener("hashchange", handleHashChange);

    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, []);

  // Events state
  const [currentTab, setCurrentTab] = useState("published");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(6);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [publishedTotal, setPublishedTotal] = useState(0);
  const [unpublishedTotal, setUnpublishedTotal] = useState(0);
  const [otpLoading, setOtpLoading] = useState(false);



  // Events data
  const [eventsData, setEventsData] = useState({
    published: [],
    unpublished: [],
  });

  // Loading states
  const [loading, setLoading] = useState({
    delete: false,
    publish: {},
    general: false,
  });

  // Modal states
  const [activeModal, setActiveModal] = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEventId, setInviteEventId] = useState(null);
  const [showNotificationsModal, setShowNotificationsModal] = useState(false);
  const [showBulkNotificationModal, setShowBulkNotificationModal] =
    useState(false);

  // Mobile menu state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Dashboard counts from API
  const [dashboardCounts, setDashboardCounts] = useState({
    attendee_events_count: 0,
    speaker_events_count: 0,
    created_events_count: 0,
  });

  // Publish confirmation modal
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [eventToPublish, setEventToPublish] = useState(null);

  // Registrations modal
  const [showRegistrationsModal, setShowRegistrationsModal] = useState(false);
  const [registrationsData, setRegistrationsData] = useState([]);
  const [registrationsLoading, setRegistrationsLoading] = useState(false);
  const [registrationsEventId, setRegistrationsEventId] = useState(null);

  // Speakers modal
  const [showSpeakersModal, setShowSpeakersModal] = useState(false);
  const [speakersData, setSpeakersData] = useState([]);
  const [speakersLoading, setSpeakersLoading] = useState(false);


  // Other states
  const [eventToDelete, setEventToDelete] = useState(null);
  const [eventToDeleteName, setEventToDeleteName] = useState("");
  const [otpValues, setOtpValues] = useState(["", "", "", "", "", ""]);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpResendCooldown, setOtpResendCooldown] = useState(0);


  // Loading skeleton state
  const [showLoadingSkeletons, setShowLoadingSkeletons] = useState(false);






  // pagination and sorting functinality 
  const [speakerPage, setSpeakerPage] = useState(1);
  const [speakerPerPage] = useState(10);
  const [speakerTotalPages, setSpeakerTotalPages] = useState(1);

  const [speakerSortColumn, setSpeakerSortColumn] = useState("created_at");
  const [speakerSortOrder, setSpeakerSortOrder] = useState("DESC");

  // search functionality 
  const [speakerSearchInput, setSpeakerSearchInput] = useState("");
  const [speakerSearch, setSpeakerSearch] = useState("");

  const [attendeeSearchInput, setAttendeeSearchInput] = useState("");
  const [attendeeSearch, setAttendeeSearch] = useState("");


  // Attendee pagination code
  const [attendeePage, setAttendeePage] = useState(1);
  const [attendeePerPage] = useState(10);
  const [attendeeTotalPages, setAttendeeTotalPages] = useState(1);


  // sorting of attendee ragistration

  const [attendeeSortColumn, setAttendeeSortColumn] = useState("created_at");
  const [attendeeSortOrder, setAttendeeSortOrder] = useState("DESC");


  useEffect(() => {
    loadMyEvents(currentTab, currentPage);
  }, [currentPage, itemsPerPage, currentTab]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSpeakerSearch(speakerSearchInput);
      setSpeakerPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [speakerSearchInput]);


  useEffect(() => {
    const timer = setTimeout(() => {
      setAttendeeSearch(attendeeSearchInput);
    }, 500);

    return () => clearTimeout(timer);
  }, [attendeeSearchInput]);

  // Refs
  const otpRefs = useRef([]);



  // Attending events state - populated from API
  const [attendingEvents, setAttendingEvents] = useState({
    upcoming: [],
    ongoing: [],
    completed: [],
  });
  const [attendingTab, setAttendingTab] = useState("upcoming");
  const [loadingAttending, setLoadingAttending] = useState(false);

  const [speakingEngagements, setSpeakingEngagements] = useState({
    approved: [],
    pending: [],
    past: [],
  });
  const [speakingTab, setSpeakingTab] = useState("pending");
  const [loadingSpeaking, setLoadingSpeaking] = useState(false);



  // Initialize
  useEffect(() => {
    if (!token || !currentUser) return;

    loadDashboardCounts();

    // Load both tabs initially
    loadMyEvents("published", 1);
    loadMyEvents("unpublished", 1);

    loadAttendingEvents();
    loadSpeakingEvents();
  }, [token]);

  // code for download csv file for ragistaration

  const downloadCSV = async (eventId) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/event_registratoin/send_report/${eventId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to download CSV");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = `event-${eventId}-registrations.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("CSV download error:", error);
      showNotification("Failed to download CSV", "error");
    }
  };


  // 1 october csv code for download attendee form


  const download_attendees_form = async (eventId) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/download_attendee_registration_customform/${eventId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to download CSV");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = `event-${eventId}-registrations.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("CSV download error:", error);
      showNotification("Failed to download CSV", "error");
    }
  };






  // code for download csv file for speaker_ragistration

  const downloadSpeakerCSV = async (eventId) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/event_registratoin/send_speaker_report/${eventId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to download speaker CSV");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = "speaker_registrations.csv";
      document.body.appendChild(a);
      a.click();

      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Speaker CSV download error:", error);
      showNotification("Failed to download speaker report", "error");
    }
  };


  // 1 october csv code for download speaker new  form


  const download_speaker_form = async (eventId) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/download_speaker_registration_customform/${eventId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to download speaker CSV");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = "speaker_registrations.csv";
      document.body.appendChild(a);
      a.click();

      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Speaker CSV download error:", error);
      showNotification("Failed to download speaker report", "error");
    }
  };





  // Load dashboard counts
  const loadDashboardCounts = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/get_my_dashboard_counts`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setDashboardCounts({
        attendee_events_count: data.attendee_events_count || 0,
        speaker_events_count: data.speaker_events_count || 0,
        created_events_count: data.created_events_count || 0,
      });
    } catch (error) {
      console.error("Error loading dashboard counts:", error);
    }
  };

  // Get event ID from event object
  const getEventId = (event) => {
    if (event && event.metadata && event.metadata.event_id) {
      return event.metadata.event_id;
    }
    return null;
  };

  // Navigate to event page
  
const navigateToEventPage = (eventId) => {
  if (eventId) {
    window.open(
      `/eventpage/${eventId}`,
      "_blank",
      "noopener,noreferrer"
    );
  } else {
    showNotification("Event ID not found", "error");
  }
};


  const navigateToGuidelines = (eventId) => {
    if (eventId) {
      navigate(`/guidelines/${eventId}`);
    } else {
      showNotification("Event ID not found", "error");
    }
  };

  const navigateToScheduler = (eventId) => {
    if (eventId) {
      navigate(`/scheduler/${eventId}`);
    } else {
      showNotification("Event ID not found", "error");
    }
  };

  // Load events function with improved loading UX
  const loadMyEvents = async (tabType, page = 1) => {
    try {
      setLoading((prev) => ({ ...prev, general: true }));
      setShowLoadingSkeletons(true);

      // Determine API endpoint based on tab
      const isPublished = tabType === "published" ? 1 : 0;
      const endpoint = `/my_event/${isPublished}?page=${page}&npp=${itemsPerPage}`;

      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Simulate a small delay for better UX (optional, remove in production)
      await new Promise((resolve) => setTimeout(resolve, 300));

      // Update events data
      setEventsData((prev) => ({
        ...prev,
        [tabType]: data.result || [],
      }));

      const total = data.total || data.result?.length || 0;

      if (tabType === "published") {
        setPublishedTotal(total);
      } else {
        setUnpublishedTotal(total);
      }

      if (tabType === currentTab) {
        setTotalItems(total);
        setTotalPages(Math.ceil(total / itemsPerPage));
      }
    } catch (error) {
      console.error("Error loading events:", error);
      showNotification("Failed to load events. Please login again.", "error");

      // Set empty arrays instead of dummy data
      setEventsData((prev) => ({
        ...prev,
        [tabType]: [],
      }));
      setTotalItems(0);
      setTotalPages(1);
    } finally {
      setLoading((prev) => ({ ...prev, general: false }));
      // Keep skeletons visible for a moment for smooth transition
      setTimeout(() => setShowLoadingSkeletons(false), 300);
    }
  };

  // Load attending events from API



  const loadAttendingEvents = async () => {
    if (!token) return;

    setLoadingAttending(true);

    try {
      const response = await fetch(
        `${API_BASE_URL}/my_registered_events_as_attendee`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();

      const events = data.result || data.data || data || [];

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const upcoming = [];
      const ongoing = [];
      const completed = [];

      events.forEach((event) => {
        const meta = event.metadata || event;

        const start = new Date(meta.start_date);
        const end = new Date(meta.end_date);

        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);

        if (today < start) {
          upcoming.push(event);
        } else if (today > end) {
          completed.push(event);
        } else {
          ongoing.push(event);
        }
      });

      console.log("API Response", events);
      console.log("Upcoming", upcoming);
      console.log("Ongoing", ongoing);
      console.log("Completed", completed);



      setAttendingEvents({
        upcoming,
        ongoing,
        completed,
      });
    } catch (error) {
      console.error(error);
      showNotification("Failed to load attending events.", "error");
    } finally {
      setLoadingAttending(false);
    }
  };










  // Load speaking engagements from API
  const loadSpeakingEvents = async () => {
  if (!token) {
    setSpeakingEngagements({
      pending: [],
      approved: [],
      past: [],
    });
    setLoadingSpeaking(false);
    return;
  }

  setLoadingSpeaking(true);

  try {
    const response = await fetch(
      `${API_BASE_URL}/my_registered_events_as_speaker`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    console.log("Speaking Engagement API Response:", data);

    // Normalize and validate each category independently.
    const normalizeEvents = (events, category) => {
      if (!Array.isArray(events)) {
        console.warn(
          `Expected ${category} to be an array:`,
          events
        );
        return [];
      }

      // Remove duplicate IDs within the same category.
      const uniqueEvents = new Map();

      events.forEach((event) => {
        if (!event || event.id == null) {
          console.warn(`Invalid event in ${category}:`, event);
          return;
        }

        if (!uniqueEvents.has(String(event.id))) {
          uniqueEvents.set(String(event.id), event);
        }
      });

      return Array.from(uniqueEvents.values());
    };

    const pendingEvents = normalizeEvents(
      data.pendingEvents,
      "pendingEvents"
    );

    const approvedEvents = normalizeEvents(
      data.approvedEvents,
      "approvedEvents"
    );

    const pastEvents = normalizeEvents(
      data.pastEvents,
      "pastEvents"
    );

    // Check whether the API categorizes the same event more than once.
    const pendingIds = new Set(
      pendingEvents.map((event) => String(event.id))
    );

    const approvedIds = new Set(
      approvedEvents.map((event) => String(event.id))
    );

    const pastIds = new Set(
      pastEvents.map((event) => String(event.id))
    );

    console.log("Pending IDs:", [...pendingIds]);
    console.log("Approved IDs:", [...approvedIds]);
    console.log("Past IDs:", [...pastIds]);

    console.log("Category overlaps:", {
      pendingAndApproved: [...pendingIds].filter((id) =>
        approvedIds.has(id)
      ),
      pendingAndPast: [...pendingIds].filter((id) =>
        pastIds.has(id)
      ),
      approvedAndPast: [...approvedIds].filter((id) =>
        pastIds.has(id)
      ),
    });

    // Keep each API category separate.
    setSpeakingEngagements({
      pending: pendingEvents,
      approved: approvedEvents,
      past: pastEvents,
    });
  } catch (error) {
    console.error("Error loading speaking events:", error);

    showNotification(
      "Failed to load speaking engagements.",
      "error"
    );
  } finally {
    setLoadingSpeaking(false);
  }
};




  // handleEditEvent function
  const handleEditEvent = (eventId) => {
    if (!eventId) {
      console.error("No event ID provided");
      // Custom styled alert
      const alertDiv = document.createElement("div");
      alertDiv.innerHTML = `
            <div style="
                position: fixed;
                top: 20px;
                right: 20px;
                background: #fff3cd;
                color: #856404;
                padding: 12px 16px;
                border: 1px solid #ffeaa7;
                border-radius: 5px;
                box-shadow: 0 2px 10px rgba(0,0,0,0.1);
                z-index: 1000;
            ">
                <i class="fas fa-exclamation-triangle" style="margin-right: 8px;"></i>
                Event ID is missing
            </div>
        `;
      document.body.appendChild(alertDiv);
      setTimeout(() => alertDiv.remove(), 3000);
      return;
    }

    // Custom confirm dialog
    const dialogDiv = document.createElement("div");
    dialogDiv.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0,0,0,0.4);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 1000;
    `;

    dialogDiv.innerHTML = `
        <div style="
            background: white;
            padding: 20px;
            border-radius: 5px;
            width: 300px;
        ">
            <p style="margin: 0 0 20px;">Edit this event?</p>
            <div style="text-align: right;">
                <button id="cancel" style="
                    padding: 8px 16px;
                    margin-right: 10px;
                    background: #f8f9fa;
                    border: 1px solid #dee2e6;
                    border-radius: 4px;
                    cursor: pointer;
                ">Cancel</button>
                <button id="ok" style="
                    padding: 8px 16px;
                    background: #007bff;
                    color: white;
                    border: none;
                    border-radius: 4px;
                    cursor: pointer;
                ">OK</button>
            </div>
        </div>
    `;

    document.body.appendChild(dialogDiv);

    dialogDiv.querySelector("#cancel").onclick = () => {
      document.body.removeChild(dialogDiv);
    };

    dialogDiv.querySelector("#ok").onclick = () => {
      document.body.removeChild(dialogDiv);
      navigate(`/edit-event/${eventId}`);
    };
  };


  // ********************************************************************************************




  // Initialize delete modal
  const handleInitiateDelete = async (eventId, eventName) => {
    // Reset all OTP-related states
    setEventToDelete(eventId);
    setEventToDeleteName(eventName || "");
    setOtpValues(["", "", "", "", "", ""]);
    setOtpVerified(false);
    setOtpSent(false);
    setOtpLoading(false); // ⭐ Reset loading state

    // Clear old timer
    if (window.otpTimer) {
      clearInterval(window.otpTimer);
      window.otpTimer = null;
    }

    // Open modal
    setActiveModal("deleteEventModal");

    // Send OTP immediately (no setTimeout)
    await handleSendDeleteOtp(eventId);
  };

  // Send OTP to email for deletion
  // Send OTP to email for deletion

  const handleSendDeleteOtp = async (eventId) => {
    if (!token || !eventId) {
      showNotification("Unable to send OTP", "error");
      setOtpLoading(false);
      setOtpSent(false);
      return;
    }

    try {
      // Start loading
      setOtpLoading(true);
      setOtpSent(false);

      const response = await fetch(`${API_BASE_URL}/event/sendotptodelete`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ eventid: eventId }),
      });

      if (response.ok) {
        const data = await response.json();

        // ⭐ CRITICAL FIX: Use setTimeout to ensure state updates
        setTimeout(() => {
          setOtpLoading(false);
          setOtpSent(true);
        }, 0);

        setOtpResendCooldown(60);

        // Clear previous timer
        if (window.otpTimer) {
          clearInterval(window.otpTimer);
        }

        // Start cooldown timer
        window.otpTimer = setInterval(() => {
          setOtpResendCooldown((prev) => {
            if (prev <= 1) {
              clearInterval(window.otpTimer);
              window.otpTimer = null;
              return 0;
            }
            return prev - 1;
          });
        }, 1000);

        showNotification(data.message || "OTP sent to your email", "success");
      } else {
        const errorData = await response.json();
        setOtpLoading(false);
        setOtpSent(false);
        showNotification(errorData.message || "Failed to send OTP", "error");
      }
    } catch (error) {
      console.error("Error sending OTP:", error);
      setOtpLoading(false);
      setOtpSent(false);
      showNotification("Error sending OTP", "error");
    }
  };






  // Verify OTP
  const handleVerifyOtp = async () => {
    if (!token || !eventToDelete) {
      showNotification("Unable to verify OTP", "error");
      return;
    }

    const enteredOtp = otpValues.join("");
    if (enteredOtp.length !== 6) {
      showNotification("Please enter 6-digit OTP", "error");
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/event/verifydeleteotp`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          eventid: eventToDelete,
          otp: enteredOtp,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setOtpVerified(true);
        showNotification(data.message || "OTP verified successfully!", "success");
      } else {
        const errorData = await response.json();
        showNotification(errorData.message || "Invalid OTP", "error");
        // Clear OTP inputs on failure
        setOtpValues(["", "", "", "", "", ""]);
        // Focus first input
        setTimeout(() => {
          if (otpRefs.current[0]) {
            otpRefs.current[0].focus();
          }
        }, 100);
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
      showNotification("Error verifying OTP", "error");
    }
  };

  // Delete an event (final step)
  const handleDeleteEvent = async () => {
    if (!token || !eventToDelete) {
      showNotification("Unable to delete event", "error");
      return;
    }

    if (!otpVerified) {
      showNotification("Please verify OTP first", "error");
      return;
    }

    setLoading((prev) => ({ ...prev, delete: true }));

    try {
      const response = await fetch(`${API_BASE_URL}/event/${eventToDelete}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        // Determine which tab the deleted event belongs to
        let deletedEventStatus = "unpublished";
        const foundEvent =
          eventsData.published.find((e) => getEventId(e) === eventToDelete) ||
          eventsData.unpublished.find((e) => getEventId(e) === eventToDelete);

        if (foundEvent && foundEvent.metadata?.is_publish === 1) {
          deletedEventStatus = "published";
        }

        // Reload the appropriate event list
        await loadMyEvents(deletedEventStatus, currentPage);

        showNotification("Event deleted successfully!", "success");

        // Reset all states and close modal
        handleCloseModal();
      } else {
        const errorData = await response.json();
        showNotification(errorData.message || "Failed to delete event", "error");
      }
    } catch (error) {
      console.error("Error deleting event:", error);
      showNotification("Error deleting event", "error");
    } finally {
      setLoading((prev) => ({ ...prev, delete: false }));
    }
  };

  // Handle OTP input changes
  const handleOtpChange = (index, value) => {
    if (value.length > 1) return;

    const newValues = [...otpValues];
    newValues[index] = value.replace(/[^0-9]/g, "");
    setOtpValues(newValues);

    // Auto-focus next input
    if (value && index < otpValues.length - 1) {
      const nextRef = otpRefs.current[index + 1];
      if (nextRef) {
        nextRef.focus();
      }
    }

    // Auto-verify when 6 digits are entered (optional)
    const enteredOtp = newValues.join("");
    if (enteredOtp.length === 6 && !otpVerified) {
      handleVerifyOtp();
    }
  };

  // Handle OTP keydown events
  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otpValues[index] && index > 0) {
      const prevInput = otpRefs.current[index - 1];
      if (prevInput) {
        prevInput.focus();
      }
    }
  };

  // Handle resend OTP

const handleResendOTP = async () => {
  if (otpResendCooldown > 0) {
    showNotification(
      `Please wait ${otpResendCooldown} seconds before resending`,
      "warning"
    );
    return;
  }

  if (!eventToDelete) {
    showNotification("No event selected for deletion", "error");
    return;
  }

  try {
    setOtpLoading(true);
    setOtpSent(false);

    // Reset OTP inputs before resending
    setOtpValues(["", "", "", "", "", ""]);
    setOtpVerified(false);

    const response = await fetch(
      `${API_BASE_URL}/event/resendotptodelete`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          eventid: eventToDelete,
        }),
      }
    );

    const data = await response.json();

    if (response.ok) {
      setOtpLoading(false);
      setOtpSent(true);
      setOtpResendCooldown(60);

      // Clear previous timer
      if (window.otpTimer) {
        clearInterval(window.otpTimer);
      }

      // Start resend cooldown timer
      window.otpTimer = setInterval(() => {
        setOtpResendCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(window.otpTimer);
            window.otpTimer = null;
            return 0;
          }

          return prev - 1;
        });
      }, 1000);

      showNotification(
        data.message || "OTP resent to your email",
        "success"
      );

      // Focus the first OTP input
      setTimeout(() => {
        if (otpRefs.current[0]) {
          otpRefs.current[0].focus();
        }
      }, 100);
    } else {
      setOtpLoading(false);
      setOtpSent(false);

      showNotification(
        data.message || "Failed to resend OTP",
        "error"
      );
    }
  } catch (error) {
    console.error("Error resending OTP:", error);

    setOtpLoading(false);
    setOtpSent(false);

    showNotification("Error resending OTP", "error");
  }
};



  // Close modal and cleanup
  const handleCloseModal = () => {
    setActiveModal(null);
    setEventToDelete(null);
    setEventToDeleteName("");
    setOtpValues(["", "", "", "", "", ""]);
    setOtpVerified(false);
    setOtpSent(false);
    setOtpLoading(false);
    setOtpResendCooldown(0);

    // Clear timer
    if (window.otpTimer) {
      clearInterval(window.otpTimer);
      window.otpTimer = null;
    }
  };

  // Check if modal is open
  const isModalOpen = (id) => activeModal === id;



  // ************************************************************************************************



  // Initiate publish with confirmation modal
  const handleInitiatePublish = (eventId) => {
    setEventToPublish(eventId);
    setShowPublishModal(true);
  };

  // Publish event function
  const publishEvent = async (eventId) => {
    if (!token || !eventId) {
      showNotification("Unable to publish event", "error");
      return;
    }

    setLoading((prev) => ({
      ...prev,
      publish: { ...prev.publish, [eventId]: true },
    }));

    try {
      const response = await fetch(`${API_BASE_URL}/publish_event/${eventId}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        // Reload both published and unpublished lists
        await loadMyEvents("published", 1);
        await loadMyEvents("unpublished", 1);
        showNotification("Event published successfully!", "success");
      } else {
        const errorData = await response.json();
        showNotification(
          errorData.message || "Failed to publish event",
          "error",
        );
      }
    } catch (error) {
      console.error("Error publishing event:", error);
      showNotification("Error publishing event", "error");
    } finally {
      setLoading((prev) => ({
        ...prev,
        publish: { ...prev.publish, [eventId]: false },
      }));
    }
  };

  // Open registrations modal and fetch data
  const openRegistrationsModal = async (eventId) => {
    setRegistrationsEventId(eventId);
    setRegistrationsData([]);
    setRegistrationsLoading(true);
    setShowRegistrationsModal(true);

    try {
      // ================================
      // GET CURRENT PAGE
      // ================================
      const response = await fetch(
        `${API_BASE_URL}/attendee_registration/${eventId}?page=${attendeePage}&numPerPage=${attendeePerPage}&sortByColumnName=${attendeeSortColumn}&sortBy=${attendeeSortOrder}&searchBy=${attendeeSearch}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      const list = Array.isArray(data)
        ? data
        : data.result || [];

      setRegistrationsData(list);

      // ================================
      // CHECK NEXT PAGE
      // ================================
      const nextPage = attendeePage + 1;

      const nextPageResponse = await fetch(
        `${API_BASE_URL}/attendee_registration/${eventId}?page=${nextPage}&numPerPage=${attendeePerPage}&sortByColumnName=${attendeeSortColumn}&sortBy=${attendeeSortOrder}&searchBy=${attendeeSearch}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!nextPageResponse.ok) {
        throw new Error(
          `Next page HTTP error! status: ${nextPageResponse.status}`
        );
      }

      const nextPageData = await nextPageResponse.json();

      const nextPageList = Array.isArray(nextPageData)
        ? nextPageData
        : nextPageData.result || [];

      // ================================
      // CALCULATE TOTAL PAGES
      // ================================
      if (nextPageList.length > 0) {
        // Next page exists
        setAttendeeTotalPages(attendeePage + 1);
      } else {
        // No next page
        setAttendeeTotalPages(attendeePage);
      }

    } catch (error) {
      console.error("Error loading registrations:", error);
      showNotification("Failed to load registrations.", "error");
    } finally {
      setRegistrationsLoading(false);
    }
  };


  // Open speakers modal and fetch data
  const openSpeakersModal = async (eventId) => {
    setRegistrationsEventId(eventId);
    setSpeakersData([]);
    setSpeakersLoading(true);
    setShowSpeakersModal(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/speaker_registration/${eventId}?page=${speakerPage}&numPerPage=${speakerPerPage}&sortByColumnName=${speakerSortColumn}&sortBy=${speakerSortOrder}&searchBy=${speakerSearch}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();

      const list = Array.isArray(data) ? data : data.result || [];

      setSpeakersData(list);
      // Check whether next page exists
      const nextPage = speakerPage + 1;

      const nextPageResponse = await fetch(
        `${API_BASE_URL}/speaker_registration/${eventId}?page=${nextPage}&numPerPage=${speakerPerPage}&sortByColumnName=${speakerSortColumn}&sortBy=${speakerSortOrder}&searchBy=${speakerSearch}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!nextPageResponse.ok) {
        throw new Error(
          `Next page HTTP error! status: ${nextPageResponse.status}`
        );
      }

      const nextPageData = await nextPageResponse.json();

      const nextPageList = Array.isArray(nextPageData)
        ? nextPageData
        : nextPageData.result || [];

      if (nextPageList.length > 0) {
        setSpeakerTotalPages(nextPage);
      } else {
        setSpeakerTotalPages(speakerPage);
      }

    } catch (error) {
      console.error("Error loading speakers:", error);
      showNotification("Failed to load speakers.", "error");
    } finally {
      setSpeakersLoading(false);
    }
  };




  useEffect(() => {
    if (showSpeakersModal && registrationsEventId) {
      openSpeakersModal(registrationsEventId);
    }
  }, [
    speakerPage,
    speakerSortColumn,
    speakerSortOrder,
    speakerSearch,
  ]);

  useEffect(() => {
    if (showRegistrationsModal && registrationsEventId) {
      openRegistrationsModal(registrationsEventId);
    }
  }, [attendeePage,

    attendeeSearch,
    attendeeSortColumn,
    attendeeSortOrder,
  ]);
  // Handle attendee approve/reject action
  const handleAttendeeAction = async (registrationId, action) => {
    if (!token || !registrationsEventId) return;
    try {
      const response = await fetch(
        `${API_BASE_URL}/approve_attendee_registration`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            eventId: registrationsEventId,
            registrationId,
            action,
          }),
        },
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        showNotification(err.message || "Action failed", "error");
        return;
      }
      const newStatus = action === "approve" ? "Approved" : "Rejected";
      setRegistrationsData((prev) =>
        prev.map((r) =>
          r.registration_id === registrationId
            ? { ...r, status: newStatus }
            : r,
        ),
      );
      showNotification(`Attendee ${newStatus} successfully`, "success");
    } catch (error) {
      console.error("Attendee action error:", error);
      showNotification("Action failed. Please try again.", "error");
    }
  };

  // Handle speaker approve/reject action
  const handleSpeakerAction = async (registrationId, action) => {
    if (!token || !registrationsEventId) return;
    try {
      const response = await fetch(
        `${API_BASE_URL}/approve_speaker_registration`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            eventId: registrationsEventId,
            registrationId,
            action,
          }),
        },
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        showNotification(err.message || "Action failed", "error");
        return;
      }
      const newStatus = action === "approve" ? "Approved" : "Rejected";
      setSpeakersData((prev) =>
        prev.map((s) =>
          s.registration_id === registrationId
            ? { ...s, status: newStatus }
            : s,
        ),
      );
      showNotification(`Speaker ${newStatus} successfully`, "success");
    } catch (error) {
      console.error("Speaker action error:", error);
      showNotification("Action failed. Please try again.", "error");
    }
  };




  // Pagination helpers
  const goToPage = (page) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    setCurrentPage(page);
  };

  const generatePaginationButtons = () => {
    const buttons = [];

    // Previous button
    buttons.push(
      <button
        key="prev"
        className="dashboard-btn dashboard-btn-secondary dashboard-btn-sm"
        onClick={() => goToPage(currentPage - 1)}
        disabled={currentPage === 1}
      >
        <i className="fas fa-chevron-left"></i>
      </button>,
    );

    // Always show first page
    buttons.push(
      <button
        key={1}
        className={`dashboard-btn dashboard-btn-sm ${1 === currentPage ? "dashboard-btn-primary" : "dashboard-btn-secondary"}`}
        onClick={() => goToPage(1)}
      >
        1
      </button>,
    );

    // Calculate range for pagination with ellipsis
    let startPage = Math.max(2, currentPage - 1);
    let endPage = Math.min(totalPages - 1, currentPage + 1);

    // Adjust if we're near the start
    if (currentPage <= 3) {
      startPage = 2;
      endPage = Math.min(4, totalPages - 1);
    }

    // Adjust if we're near the end
    if (currentPage >= totalPages - 2) {
      startPage = Math.max(2, totalPages - 3);
      endPage = totalPages - 1;
    }

    // Add ellipsis after first page if needed
    if (startPage > 2) {
      buttons.push(
        <span key="ellipsis1" className="pagination-ellipsis">
          ...
        </span>,
      );
    }

    // Add middle pages
    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button
          key={i}
          className={`dashboard-btn dashboard-btn-sm ${i === currentPage ? "dashboard-btn-primary" : "dashboard-btn-secondary"}`}
          onClick={() => goToPage(i)}
        >
          {i}
        </button>,
      );
    }

    // Add ellipsis before last page if needed
    if (endPage < totalPages - 1) {
      buttons.push(
        <span key="ellipsis2" className="pagination-ellipsis">
          ...
        </span>,
      );
    }

    // Always show last page if there is more than one page
    if (totalPages > 1) {
      buttons.push(
        <button
          key={totalPages}
          className={`dashboard-btn dashboard-btn-sm ${totalPages === currentPage ? "dashboard-btn-primary" : "dashboard-btn-secondary"}`}
          onClick={() => goToPage(totalPages)}
        >
          {totalPages}
        </button>,
      );
    }

    // Next button
    buttons.push(
      <button
        key="next"
        className="dashboard-btn dashboard-btn-secondary dashboard-btn-sm"
        onClick={() => goToPage(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        <i className="fas fa-chevron-right"></i>
      </button>,
    );

    return buttons;
  };

  // Notification function
  const showNotification = (message, type = "success") => {
    // Create notification element
    const notification = document.createElement("div");
    notification.className = "dashboard-notification";
    notification.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            background-color: ${type === "success" ? "#38b2ac" : type === "error" ? "#f56565" : "#4299e1"};
            color: white;
            padding: 15px 25px;
            border-radius: 5px;
            z-index: 2000;
            font-weight: 500;
            transition: all 0.3s;
            max-width: 300px;
            box-shadow: 0 5px 15px rgba(0,0,0,0.2);
            display: flex;
            align-items: center;
            gap: 10px;
        `;

    // Add icon based on type
    const icon =
      type === "success"
        ? "check-circle"
        : type === "error"
          ? "exclamation-circle"
          : "info-circle";
    notification.innerHTML = `
            <i class="fas fa-${icon}"></i>
            <span>${message}</span>
        `;

    document.body.appendChild(notification);

    // Remove notification after 3 seconds
    setTimeout(() => {
      notification.style.opacity = "0";
      notification.style.transform = "translateX(100px)";
      setTimeout(() => {
        if (notification.parentNode) {
          document.body.removeChild(notification);
        }
      }, 300);
    }, 3000);
  };

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return "Date not set";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      return "Invalid date";
    }
  };

  // Render loading skeletons
  const renderEventSkeletons = () => {
    return Array.from({ length: itemsPerPage }).map((_, index) => (
      <div className="dashboard-event-card skeleton" key={`skeleton-${index}`}>
        <div className="dashboard-event-header skeleton-header">
          <div className="dashboard-event-status skeleton-status"></div>
          <div className="dashboard-event-actions">
            {[1, 2, 3, 4].map((i) => (
              <button
                key={i}
                className="dashboard-action-btn skeleton-btn"
                disabled
              >
                <div className="skeleton-icon"></div>
              </button>
            ))}
          </div>
        </div>
        <div className="dashboard-event-image skeleton-image"></div>
        <div className="dashboard-event-content">
          <div className="dashboard-event-title skeleton-text skeleton-title"></div>
          <div className="dashboard-event-meta">
            <span className="skeleton-text skeleton-meta"></span>
            <span className="skeleton-text skeleton-meta"></span>
            <span className="skeleton-text skeleton-meta"></span>
          </div>
          <div className="dashboard-event-description">
            <div className="skeleton-text skeleton-desc"></div>
            <div className="skeleton-text skeleton-desc"></div>
          </div>
          <div className="dashboard-event-stats">
            {[1, 2, 3].map((i) => (
              <div className="dashboard-event-stat" key={i}>
                <div className="dashboard-event-stat-number skeleton-text skeleton-stat"></div>
                <div className="dashboard-event-stat-label skeleton-text skeleton-stat-label"></div>
              </div>
            ))}
          </div>
          <div className="dashboard-event-action-buttons">
            <button
              className="dashboard-btn skeleton-btn skeleton-action-btn"
              disabled
            ></button>
            <button
              className="dashboard-btn skeleton-btn skeleton-action-btn"
              disabled
            ></button>
          </div>
        </div>
      </div>
    ));
  };

  // Render event card - UPDATED with click navigation
  const renderEventCard = (event, isPublished) => {
    const eventData = event.metadata || {};
    const imageData = event.banner_file;
    const eventId = getEventId(event);
    const isFree = eventData.is_event_free === true;
    const priceLabel = isFree ? "Free" : "Paid";

    return (
      <div
        className="dashboard-event-card"
        key={eventData.event_id}
        onClick={(e) => {
          // Only navigate if click is not on action buttons
          if (
            !e.target.closest(".dashboard-event-actions") &&
            !e.target.closest(".dashboard-event-action-buttons") &&
            !e.target.closest(".dashboard-event-header")
          ) {
            navigateToEventPage(eventId);
          }
        }}
        style={{
          cursor: "pointer",
          transition: "transform 0.2s, box-shadow 0.2s",
        }}
        onMouseOver={(e) => {
          if (
            !e.target.closest(".dashboard-event-actions") &&
            !e.target.closest(".dashboard-event-action-buttons")
          ) {
            e.currentTarget.style.transform = "translateY(-4px)";
            e.currentTarget.style.boxShadow = "0 10px 20px rgba(0,0,0,0.1)";
          }
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "none";
        }}
      >
        <div className="dashboard-event-header">
          <div
            className={`dashboard-event-status ${isPublished ? "dashboard-status-published" : "dashboard-status-unpublished"}`}
          >
            {isPublished ? "Published" : "Unpublished"}
          </div>
          <div
            className="dashboard-event-actions"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="dashboard-action-btn"
              title="Edit"
              onClick={() => handleEditEvent(getEventId(event))}
            >
              <img src={editIcon} alt="edit-icon" />
            </button>
            <button
              type="button"
              className="dashboard-action-btn"
              onClick={() =>
                handleInitiateDelete(getEventId(event), eventData.name)
              }
              title="Delete"
            >
              <img src={deleteIcon} alt="delete-icon" />
            </button>
            {!isPublished && (
              <button
                className="dashboard-action-btn"
                title="Publish"
                onClick={() => handleInitiatePublish(eventData.event_id)}
                disabled={loading.publish[eventData.event_id]}
              >
                {loading.publish[eventData.event_id] ? (
                  <i className="fas fa-spinner fa-spin"></i>
                ) : (
                  <i className="fas fa-paper-plane"></i>
                )}
              </button>
            )}
            <button
              className="dashboard-action-btn"
              title="Share"
              onClick={(e) => {
                e.stopPropagation();
                setInviteEventId(eventId);
                setShowInviteModal(true);
              }}
            >
              <img src={shareIcon} alt="share-icon" />
            </button>
          </div>
        </div>
        <div className="dashboard-event-image">
          {imageData && imageData.base64 ? (
            <img src={imageData.base64} alt={eventData.name} />
          ) : (
            <div className="dashboard-event-image-placeholder">
              <i className="fas fa-calendar-alt fa-2x"></i>
            </div>
          )}
        </div>
        <div className="dashboard-event-content">
          <h3 className="dashboard-event-title">
            {eventData.name || "Untitled Event"}
          </h3>
          <div className="dashboard-event-meta">
            <span>
              <img src={dateIcon} alt="date-icon" />{" "}
              {formatDate(eventData.start_date)} -{" "}
              {formatDate(eventData.end_date)}
            </span>
            <span>
              <img src={locationIcon} alt="location-icon" />{" "}
              {eventData.city || "Location not set"}
            </span>
          </div>
          <div className="dashboard-myevent-meta type-price">
            <span className="dashboard-event-type">{eventData.event_type}</span>
            <span className="dashboard-event-type">{priceLabel}</span>
          </div>


          {/* 

          <div className="dashboard-event-stats">
            <div className="dashboard-event-stat">
              <div className="dashboard-event-stat-number">
                {eventData.attendeeCount || 0}
              </div>
              <div className="dashboard-event-stat-label">Attendees</div>
            </div>
            <div className="dashboard-event-stat">
              <div className="dashboard-event-stat-number">
                {eventData.speakerCount || 0}
              </div>
              <div className="dashboard-event-stat-label">Speakers</div>
            </div>
            <div className="dashboard-event-stat">
              <div className="dashboard-event-stat-number">
                {eventData.sponsors || 0}
              </div>
              <div className="dashboard-event-stat-label">Sponsors</div>
            </div>
          </div>

*/}



          <div
            className="dashboard-event-action-buttons"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="dashboard-btn dashboard-btn-primary dashboard-btn-sm"
            onClick={() => navigate(`/attendee-management/${eventId}`)}
            >
              <Users size={14} />
              Attendees [{eventData.attendeeCount || 0}]
            </button>


            <button
              className="dashboard-btn dashboard-btn-secondary dashboard-btn-sm"
             onClick={() => navigate(`/speaker-management/${eventId}`)}
            >
              <Mic size={14} />
              Speakers [{eventData.speakerCount || 0}]
            </button>
          </div>



          <div
            className="dashboard-event-action-buttons"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="dashboard-btn dashboard-btn-primary dashboard-btn-sm"
              onClick={() => navigateToGuidelines(eventId)}
            >
              <FileText size={14} />
              Guidelines
            </button>
            <button
              className="dashboard-btn dashboard-btn-secondary dashboard-btn-sm"
              onClick={() => navigateToScheduler(eventId)}
            >
              <CalendarDays size={14} />
              Scheduler
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Render pagination section

  const renderPagination = () => {
    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, totalItems);

    return (
      <div className="dashboard-pagination-wrapper">
        <div className="dashboard-pagination-left">
          <span className="dashboard-pagination-label">Show:</span>
          <select
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(parseInt(e.target.value));
              setCurrentPage(1);
            }}
            className="dashboard-items-per-page"
          >
            <option value="3">3</option>
            <option value="6">6</option>
            <option value="9">9</option>
            <option value="18">18</option>
          </select>
          <span className="dashboard-pagination-label">per page</span>
        </div>

        <div className="dashboard-pagination-info">
          Showing {startItem}-{endItem} of {totalItems} events
        </div>

        {totalPages > 1 && (
          <div className="dashboard-pagination-controls">
            {generatePaginationButtons()}
          </div>
        )}
      </div>
    );
  };





  // Render empty state
  const renderEmptyState = (tabType) => (
    <div className="dashboard-empty-state">
      <i className="fas fa-calendar-times"></i>
      <h3>No {tabType} events</h3>
      <p>
        {tabType === "published"
          ? "Your published events will appear here"
          : "Your unpublished events will appear here"}
      </p>
      {tabType === "unpublished" && (
        <button
          className="dashboard-btn dashboard-btn-primary"
          onClick={() => navigate("/create-event")}
        >
          <i className="fas fa-plus"></i> Create Event
        </button>
      )}
    </div>
  );

  // Render attending event card - uses API data
  const renderAttendingEventCard = (item, tabName) => {
    const eventMeta = item.metadata || item;
    const eventId = item.metadata?.event_id || item.event_id || item.id;

  
const bannerBase64 = item?.banner_file?.base64;

const bannerImage =
  typeof bannerBase64 === "string" &&
  bannerBase64.trim() !== "" &&
  !bannerBase64.includes("base64,undefined") &&
  !bannerBase64.includes("base64,null")
    ? bannerBase64
    : "/eventfork_banner.jpg";


    const title = eventMeta.name || eventMeta.title || "Untitled Event";
    const city = eventMeta.city || "Location not set";
    const eventType = eventMeta.event_type || eventMeta.type || "Event";
    const isFree =
      eventMeta.is_free === 1 ||
      eventMeta.is_event_free === true ||
      eventMeta.price === 0 ||
      eventMeta.price === "0" ||
      eventMeta.price === "Free";
    const priceLabel = isFree ? "Free" : "Paid";

    const startDate = formatDate(
      eventMeta.schedule_start_date ||
      eventMeta.start_date ||
      eventMeta.startDate
    );

    const endDate = formatDate(
      eventMeta.schedule_end_date ||
      eventMeta.end_date ||
      eventMeta.endDate
    );
    const statusLabel = tabName === "completed" ? "Complete" : "Upcoming";
    const statusClass =
      tabName === "completed"
        ? "dashboard-status-completed"
        : "dashboard-status-upcoming";

    return (
      <div
        className="dashboard-attending-event-card"
        key={eventId || Math.random()}
        style={{ cursor: "pointer" }}
        onClick={() => eventId && window.open(
  `/eventpage/${eventId}`,
  "_blank",
  "noopener,noreferrer"
)}
      >
        
        <div className="dashboard-attending-event-header">
          <div className={`dashboard-attending-event-status ${statusClass}`}>
            {statusLabel}
          </div>
          <button
            className="dashboard-action-btn"
            title="Share"
            onClick={(e) => {
              e.stopPropagation();
              setInviteEventId(eventId);
              setShowInviteModal(true);
            }}
          >
            <img src={shareIcon} alt="share-icon" />
          </button>
        </div>
        <div className="dashboard-event-image">
          {bannerBase64 ? (
            <img src={bannerBase64} alt={title} />
          ) : (
            <div className="dashboard-event-image-placeholder">
              <i className="fas fa-calendar-alt fa-2x"></i>
            </div>
          )}
        </div>
        <div className="dashboard-attending-event-content">
          <h3 className="dashboard-attending-event-title">{title}</h3>
          <div className="dashboard-attending-event-meta date-location">
            <span>
              <img src={dateIcon} alt="date-icon" /> {startDate}
              {endDate !== startDate ? ` - ${endDate}` : ""}
            </span>
            <span>
              <img src={locationIcon} alt="location-icon" /> {city}
            </span>
          </div>
          <div className="dashboard-attending-event-meta type-price">
            <span className="dashboard-event-type">{eventType}</span>
            <span className="dashboard-event-type">{priceLabel}</span>
          </div>
          <div className="dashboard-attending-event-actions">
            <button
              className="dashboard-btn dashboard-btn-primary dashboard-btn-sm"
              onClick={(e) => {
                e.stopPropagation();
                if (eventId) navigate(`/eventpage/${eventId}`);
              }}
            >
              View Details
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Render speaking engagement card - uses API data
  const renderSpeakingCard = (item, tabName) => {
    const eventMeta = item.metadata || item;
    const eventId = item.metadata?.event_id || item.event_id || item.id;
    const bannerBase64 = item.banner_file?.base64 || null;

    const title = eventMeta.name || eventMeta.title || "Untitled Event";
    const description = eventMeta.description || "No description available.";
    const city = eventMeta.city || "Location not set";
    const eventType = eventMeta.event_type || eventMeta.type || "Event";
    const startDate = formatDate(
      eventMeta.schedule_start_date ||
      eventMeta.start_date ||
      eventMeta.startDate
    );

    const endDate = formatDate(
      eventMeta.schedule_end_date ||
      eventMeta.end_date ||
      eventMeta.endDate
    );
    const statusLabel =
      tabName === "pending"
        ? "Pending"
        : tabName === "approved"
          ? "Approved"
          : "Past";
    const statusClass =
      tabName === "pending"
        ? "dashboard-status-unpublished"
        : tabName === "approved"
          ? "dashboard-status-published"
          : "dashboard-status-completed";

    return (
      <div
        className="dashboard-speaking-card"
        key={eventId || Math.random()}
        style={{ cursor: "pointer" }}
        onClick={() => eventId && window.open(
  `/eventpage/${eventId}`,
  "_blank",
  "noopener,noreferrer"
)}
      >
        <div className="dashboard-speaking-header">
          <div className={`dashboard-speaking-status ${statusClass}`}>
            {statusLabel}
          </div>
          <div className="dashboard-event-type">{eventType}</div>
        </div>
        <h3 className="dashboard-speaking-title">{title}</h3>
        <div className="dashboard-speaking-meta">
          <span>
            <img src={dateIcon} alt="date-icon" /> {startDate}
            {endDate !== startDate ? ` – ${endDate}` : ""}
          </span>
        </div>
        <div className="dashboard-speaking-meta">
          <span>
            <img src={locationIcon} alt="location-icon" /> {city}
          </span>
        </div>
        <p className="dashboard-speaking-description">{description}</p>
        <div
          className="dashboard-speaking-actions"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className="dashboard-btn dashboard-btn-primary dashboard-btn-sm"
            onClick={() => eventId && window.open(
  `/eventpage/${eventId}`,
  "_blank",
  "noopener,noreferrer"
)}
          >
            View Details
          </button>
        </div>
      </div>
    );
  };

  if (!currentUser) {
    return null;
  }

  const [isExpanded, setIsExpanded] = useState(false);
  const getInitials = (name = "") => {
    const parts = name.trim().split(" ");
    if (parts.length === 1) return parts[0][0]?.toUpperCase();
    return (
      parts[0][0]?.toUpperCase() + parts[parts.length - 1][0]?.toUpperCase()
    );
  };

  return (
    <div>
      <div className="dashboard-container">
        {/* Sidebar */}
        <div
          className={`dashboard-sidebar ${isExpanded ? "sidebar-desktop-active" : "sidebar-mobile-active"
            }`}
        >
          <button
            className="dashboard-hamburger"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <i className={`fas ${isExpanded ? "fa-times" : "fa-bars"}`}></i>
          </button>
          {!isExpanded && (
            <div className="nav-mobile-view">
              <div className="dashboard-user-avatar">
                {getInitials(currentUser.name || "John Smith")}
              </div>
              <div className="dashboard-nav-menu">
                {[
                  "dashboard",
                  "my-events",
                  "attending",
                  "speaking",
                  "notifications",
                  "settings",
                ].map((section) => (
                  <a
                    key={section}
                    href={`#${section}`}
                    className={`dashboard-nav-item ${activeSection === section ? "dashboard-nav-item-active" : ""}`}
                    onClick={(e) => {
                      e.preventDefault();

                      setActiveSection(section);
                      setMobileMenuOpen(false);

                      if (section !== "settings") {
                        const el = document.getElementById(section);
                        if (el) el.scrollIntoView({ behavior: "smooth" });
                      }
                    }}
                  >
                    <i
                      className={`fas fa-${section === "dashboard"
                        ? "tachometer-alt"
                        : section === "my-events"
                          ? "calendar-check"
                          : section === "attending"
                            ? "ticket-alt"
                            : section === "speaking"
                              ? "microphone"
                              : section === "notifications"
                                ? "bell"
                                : "cog"
                        }`}
                    ></i>
                  </a>
                ))}
              </div>
            </div>
          )}
          {isExpanded && (
            <div className="nav-desktop-view">
              <div className="dashboard-user-profile">
                <div className="dashboard-user-avatar">
                  {getInitials(currentUser.name || "John Smith")}
                </div>
                <div className="dashboard-user-name">
                  {currentUser.name || "John Smith"}
                </div>
                <div className="dashboard-user-role">Organizer & Speaker</div>
                <div className="dashboard-user-stats">
                  <div className="dashboard-stat-item event">
                    <div className="dashboard-stat-number">
                      {dashboardCounts.created_events_count < 10
                        ? `0${dashboardCounts.created_events_count}`
                        : dashboardCounts.created_events_count}
                    </div>
                    <div className="dashboard-stat-label">Events</div>
                  </div>
                  <div className="dashboard-stat-item attend">
                    <div className="dashboard-stat-number">
                      {dashboardCounts.attendee_events_count < 10
                        ? `0${dashboardCounts.attendee_events_count}`
                        : dashboardCounts.attendee_events_count}
                    </div>
                    <div className="dashboard-stat-label">Attending</div>
                  </div>
                  <div className="dashboard-stat-item talk">
                    <div className="dashboard-stat-number">
                      {dashboardCounts.speaker_events_count < 10
                        ? `0${dashboardCounts.speaker_events_count}`
                        : dashboardCounts.speaker_events_count}
                    </div>
                    <div className="dashboard-stat-label">Talks</div>
                  </div>
                </div>
              </div>

              <div className="dashboard-nav-menu">
                {[
                  "dashboard",
                  "my-events",
                  "attending",
                  "speaking",
                  "notifications",
                  "settings",
                ].map((section) => (
                  <a
                    key={section}
                    href={`#${section}`}
                    className={`dashboard-nav-item ${activeSection === section ? "dashboard-nav-item-active" : ""}`}
                    onClick={(e) => {
                      e.preventDefault();

                      setActiveSection(section);
                      setMobileMenuOpen(false);

                      if (section !== "settings") {
                        const el = document.getElementById(section);
                        if (el) el.scrollIntoView({ behavior: "smooth" });
                      }
                    }}
                  >
                    <i
                      className={`fas fa-${section === "dashboard"
                        ? "tachometer-alt"
                        : section === "my-events"
                          ? "calendar-check"
                          : section === "attending"
                            ? "ticket-alt"
                            : section === "speaking"
                              ? "microphone"
                              : section === "notifications"
                                ? "bell"
                                : "cog"
                        }`}
                    ></i>
                    {section
                      .split("-")
                      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                      .join(" ")}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Main Content */}
        <div
          className={`dashboard-main-content  ${isExpanded ? "sidebar-desktop-active" : "sidebar-mobile-active"
            }`}
        >
          {/* Top Header */}
          {/* <div className="dashboard-top-header">
          <div
            className="dashboard-mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <i className="fas fa-bars"></i>
          </div>

          <div className="dashboard-search-container">
            <i className="fas fa-search"></i>
            <input type="text" placeholder="Search events, attendees..." />
          </div>

          <div className="dashboard-header-actions">
            <div
              className="dashboard-notification-bell"
              onClick={() => setShowNotificationsModal(true)}
            >
              <i className="far fa-bell"></i>
              <div className="dashboard-notification-badge">3</div>
            </div>
            <button className="dashboard-btn dashboard-btn-primary">
              <i className="fas fa-plus"></i> Create Event
            </button>
          </div>
        </div> */}

          {/* Dashboard Content */}
          <div className="dashboard-content">


            {activeSection === "settings" ? (
              <ProfilePage />   // 🔥 SETTINGS PAGE
            ) : (
              <>



                {/* Welcome Section */}
                <div id="dashboard" className="dashboard-welcome-section">
                  <h1 className="dashboard-welcome-title">
                    Welcome back, {currentUser.name || "John"}!
                  </h1>
                  <p className="dashboard-welcome-subtitle">
                    Here's what's happening with your events today.
                  </p>
                </div>

                {/* Quick Stats */}
                <div className="dashboard-stats-grid">
                  <div className="dashboard-stat-card organizer">
                    <div className="dashboard-stat-icon dashboard-stat-organizer">
                      <img src={checkboxIcon} alt="checkbox-icon" />
                    </div>
                    <div className="dashboard-stat-info">
                      <h3>
                        {dashboardCounts.created_events_count < 10
                          ? `0${dashboardCounts.created_events_count}`
                          : dashboardCounts.created_events_count}
                      </h3>
                      <p>
                        Events <br /> Organized
                      </p>
                    </div>
                  </div>
                  <div className="dashboard-stat-card events">
                    <div className="dashboard-stat-icon dashboard-stat-events">
                      <img src={ticketIcon} alt="ticket-icon" />
                    </div>
                    <div className="dashboard-stat-info">
                      <h3>
                        {dashboardCounts.attendee_events_count < 10
                          ? `0${dashboardCounts.attendee_events_count}`
                          : dashboardCounts.attendee_events_count}
                      </h3>
                      <p>
                        Events <br /> Attending
                      </p>
                    </div>
                  </div>
                  <div className="dashboard-stat-card speaker">
                    <div className="dashboard-stat-icon dashboard-stat-speaker">
                      <img src={micIcon} alt="mic-icon" />
                    </div>
                    <div className="dashboard-stat-info">
                      <h3>
                        {dashboardCounts.speaker_events_count < 10
                          ? `0${dashboardCounts.speaker_events_count}`
                          : dashboardCounts.speaker_events_count}
                      </h3>
                      <p>
                        Speaking <br /> Engagements
                      </p>
                    </div>
                  </div>
                </div>




                {/* My Events Section */}
                <div id="my-events" className="dashboard-section">
                  <div className="dashboard-section-header">
                    <h2 className="dashboard-section-title">
                      <img src={checkboxIcon} alt="checkbox-icon" /> My Event
                    </h2>
                    <div className="dashboard-section-actions">
                      <button
                        className="dashboard-btn dashboard-btn-primary"
                        onClick={() => navigate("/create-event")}
                      >
                        <img src={createPlusIcon} alt="plus-icon" /> Create Event
                      </button>
                      <button
                        className="dashboard-btn dashboard-btn-secondary"
                        onClick={() => loadMyEvents(currentTab, currentPage)}
                      >
                        <img src={circlerightIcon} alt="refresh-icon" /> Refresh
                      </button>
                    </div>
                  </div>

                  {/* Tabs */}
                  <div className="dashboard-tabs">
                    <button
                      className={`dashboard-tab ${currentTab === "published" ? "dashboard-tab-active" : ""}`}
                      onClick={() => {
                        setCurrentTab("published");
                        setCurrentPage(1);
                      }}
                    >
                      Published ({publishedTotal})
                    </button>
                    <button
                      className={`dashboard-tab ${currentTab === "unpublished" ? "dashboard-tab-active" : ""}`}
                      onClick={() => {
                        setCurrentTab("unpublished");
                        setCurrentPage(1);
                      }}
                    >
                      Unpublished ({unpublishedTotal})
                    </button>
                  </div>

                  {/* Tab Content with improved loading */}
                  <div className="dashboard-tab-content">
                    {loading.general || showLoadingSkeletons ? (
                      <div className="dashboard-events-grid">
                        {renderEventSkeletons()}
                      </div>
                    ) : (
                      <>
                        {currentTab === "published" ? (
                          <>
                            <div className="dashboard-events-grid">
                              {eventsData.published.length > 0
                                ? eventsData.published.map((event) =>
                                  renderEventCard(event, true),
                                )
                                : renderEmptyState("published")}
                            </div>
                            {eventsData.published.length > 0 && renderPagination()}
                          </>
                        ) : (
                          <>
                            <div className="dashboard-events-grid">
                              {eventsData.unpublished.length > 0
                                ? eventsData.unpublished.map((event) =>
                                  renderEventCard(event, false),
                                )
                                : renderEmptyState("unpublished")}
                            </div>
                            {eventsData.unpublished.length > 0 && renderPagination()}
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Attending Events Section */}
                <div id="attending" className="dashboard-section attending-events">
                  <div className="dashboard-section-header">
                    <h2 className="dashboard-section-title">
                      <img src={ticketIcon} alt="ticket-icon" /> Events I'm Attending
                    </h2>
                    <div className="dashboard-section-actions">
                      <button
                        className="dashboard-btn dashboard-btn-primary"
                        onClick={() => navigate("/all-event")}
                      >
                        <i className="fas fa-search"></i> Browse Event
                      </button>
                    </div>
                  </div>

                  <div className="dashboard-tabs">
                    <button
                      className={`dashboard-tab ${attendingTab === "upcoming" ? "dashboard-tab-active" : ""}`}
                      onClick={() => setAttendingTab("upcoming")}
                    >
                      Upcoming ({attendingEvents.upcoming.length})
                    </button>
                    <button
                      className={`dashboard-tab ${attendingTab === "completed" ? "dashboard-tab-active" : ""}`}
                      onClick={() => setAttendingTab("completed")}
                    >
                      Completed ({attendingEvents.completed.length})
                    </button>
                  </div>

                  <div className="dashboard-tab-content">
                    {loadingAttending ? (
                      <div style={{ textAlign: "center", padding: "40px" }}>
                        <i className="fas fa-spinner fa-spin fa-2x"></i>
                        <p>Loading events...</p>
                      </div>
                    ) : attendingEvents[attendingTab].length > 0 ? (
                      <div className="dashboard-attending-events-grid">
                        {attendingEvents[attendingTab].map((event) =>
                          renderAttendingEventCard(event, attendingTab),
                        )}
                      </div>
                    ) : (
                      <div className="dashboard-empty-state">
                        <i className="fas fa-ticket-alt"></i>
                        <h3>No {attendingTab} events</h3>
                        <p>Events you register for will appear here.</p>
                      </div>
                    )}
                  </div>
                </div>
                

                {/* Speaking Engagements Section */}
                <div id="speaking" className="dashboard-section speaking-engagements">
                  <div className="dashboard-section-header">
                    <h2 className="dashboard-section-title">
                      <img src={micIcon} alt="mic-icon" /> Speaking Engagements
                    </h2>
                    <div className="dashboard-section-actions">
                      <button
                        className="dashboard-btn dashboard-btn-primary"
                        onClick={() => navigate("/all-event")}
                      >
                        <i className="fas fa-search"></i> Browse Event
                      </button>
                    </div>
                  </div>

                  <div className="dashboard-tabs">
                    <button
                      className={`dashboard-tab ${speakingTab === "pending" ? "dashboard-tab-active" : ""}`}
                      onClick={() => setSpeakingTab("pending")}
                    >
                      Pending ({speakingEngagements.pending.length})
                    </button>
                    <button
                      className={`dashboard-tab ${speakingTab === "approved" ? "dashboard-tab-active" : ""}`}
                      onClick={() => setSpeakingTab("approved")}
                    >
                      Approved ({speakingEngagements.approved.length})
                    </button>
                    <button
                      className={`dashboard-tab ${speakingTab === "past" ? "dashboard-tab-active" : ""}`}
                      onClick={() => setSpeakingTab("past")}
                    >
                      Past ({speakingEngagements.past.length})
                    </button>
                  </div>

                  <div className="dashboard-tab-content">
                    {loadingSpeaking ? (
                      <div style={{ textAlign: "center", padding: "40px" }}>
                        <i className="fas fa-spinner fa-spin fa-2x"></i>
                        <p>Loading speaking engagements...</p>
                      </div>
                    ) : speakingEngagements[speakingTab].length > 0 ? (
                      <div className="dashboard-speaking-grid">
                        {speakingEngagements[speakingTab].map((item) =>
                          renderSpeakingCard(item, speakingTab),
                        )}
                      </div>
                    ) : (
                      <div className="dashboard-empty-state">
                        <i className="fas fa-microphone-slash"></i>
                        <h3>No {speakingTab} engagements</h3>
                        <p>Your speaking engagements will appear here.</p>
                      </div>
                    )}
                  </div>
                </div>

              </>
            )}
          </div>
        </div>







        {/* ************************************************************************************ */}

        {/* Delete Event Modal */}
        {isModalOpen("deleteEventModal") && (
          <div className="dashboard-modal">
            <div className="dashboard-modal-content">
              <div className="dashboard-modal-header">
                <h3>Delete Event</h3>
                <button
                  type="button"
                  className="dashboard-close-modal"
                  onClick={handleCloseModal}
                >
                  &times;
                </button>
              </div>
              <div className="dashboard-modal-body">
                <div className="warning-message">
                  <h4>Warning: This action cannot be undone</h4>
                  <p>
                    All event data, including registrations, speakers, and
                    sponsors will be permanently deleted.
                  </p>
                </div>

                {/* ✅ FIXED: Proper conditional rendering for all states */}
                {otpLoading ? (
                  /* State 1: Loading - OTP is being sent */
                  <div className="otp-sending">
                    <i className="fas fa-spinner fa-spin"></i>
                    <p>Sending OTP to your email...</p>
                  </div>
                ) : otpSent ? (
                  /* State 2: OTP Sent - Show verification form */
                  <>
                    <p>
                      For security purposes, please enter the 6-digit OTP sent to
                      your registered email to confirm deletion.
                    </p>

                    <div className="otp-container">
                      {otpValues.map((value, index) => (
                        <input
                          key={index}
                          id={`otp-input-${index}`}
                          type="text"
                          maxLength={1}
                          className="dashboard-form-input otp-input"
                          value={value}
                          onChange={(e) => handleOtpChange(index, e.target.value)}
                          onKeyDown={(e) => handleOtpKeyDown(index, e)}
                          ref={(el) => (otpRefs.current[index] = el)}
                          disabled={otpVerified}
                          autoFocus={index === 0 && !otpVerified}
                        />
                      ))}
                    </div>

                    {!otpVerified && (
                      <div className="otp-actions">
                        <button
                          type="button"
                          className="dashboard-btn dashboard-btn-primary"
                          onClick={handleVerifyOtp}
                          disabled={otpValues.join("").length !== 6}
                        >
                          <i className="fas fa-check-circle"></i> Verify OTP
                        </button>
                        <button
                          type="button"
                          className="dashboard-btn dashboard-btn-secondary"
                          onClick={handleResendOTP}
                          disabled={otpResendCooldown > 0}
                        >
                          <i className="fas fa-redo"></i>
                          {otpResendCooldown > 0
                            ? ` Resend (${otpResendCooldown}s)`
                            : " Resend OTP"}
                        </button>
                      </div>
                    )}

                    {otpVerified && (
                      <>
                        <div className="otp-success">
                          <i className="fas fa-check-circle"></i> OTP verified
                          successfully! You can now delete the event.
                        </div>
                        <div className="form-actions">
                          <button
                            type="button"
                            className="dashboard-btn dashboard-btn-secondary"
                            onClick={handleCloseModal}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            className="dashboard-btn dashboard-btn-danger"
                            onClick={handleDeleteEvent}
                            disabled={!otpVerified || loading.delete}
                          >
                            {loading.delete ? (
                              <>
                                <i className="fas fa-spinner fa-spin"></i> Deleting...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-trash"></i> Delete Event
                              </>
                            )}
                          </button>
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  /* State 3: Initial State - No OTP sent yet (optional waiting state) */
                  <div className="otp-waiting">
                    <i className="fas fa-envelope"></i>
                    <p>Preparing to send verification code...</p>
                    <small>Please wait a moment</small>
                  </div>
                )}

                {/* Show cancel button in all states except when OTP is verified (it's in the verified section) */}
                {!otpVerified && otpSent && !otpLoading && (
                  <div className="form-actions" style={{ marginTop: "1rem" }}>
                    <button
                      type="button"
                      className="dashboard-btn dashboard-btn-secondary"
                      onClick={handleCloseModal}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}


        {/* **************************************************************************************************************** */}
        {/* Invite Modal */}
        {showInviteModal && (
          <div
            className="dashboard-modal"
            onClick={(e) =>
              e.target.className === "dashboard-modal" &&
              setShowInviteModal(false)
            }
          >
            <div className="dashboard-modal-content">
              <div className="dashboard-modal-header">
                <h3>Shareable  links</h3>

                <button
                  className="dashboard-close-modal"
                  onClick={() => setShowInviteModal(false)}
                >
                  &times;
                </button>
              </div>

              <div className="dashboard-modal-body">

                {/* Event Link */}
                <div className="dashboard-form-group">
                  <label className="dashboard-form-label">
                    Event Link
                  </label>

                  <div className="dashboard-input-group">
                    <input
                      type="text"
                      className="dashboard-form-input"
                      value={`${window.location.origin}/eventpage/${inviteEventId}`}
                      readOnly
                    />

                    <button
                      className="dashboard-btn dashboard-btn-primary"
                      onClick={() => {
                        const link = `${window.location.origin}/eventpage/${inviteEventId}`;

                        navigator.clipboard.writeText(link);

                        showNotification(
                          "Event link copied to clipboard!",
                          "success"
                        );
                      }}
                    >
                      <i className="fas fa-copy"></i> Copy
                    </button>
                  </div>
                </div>


                {/* Attendee Registration Link */}
                <div className="dashboard-form-group">
                  <label className="dashboard-form-label">
                    Attendee Registration Link
                  </label>

                  <div className="dashboard-input-group">
                    <input
                      type="text"
                      className="dashboard-form-input"
                      value={`${window.location.origin}/free-event-register/event/${inviteEventId}`}
                      readOnly
                    />

                    <button
                      className="dashboard-btn dashboard-btn-primary"
                      onClick={() => {
                        const link = `${window.location.origin}/free-event-register/event/${inviteEventId}`;

                        navigator.clipboard.writeText(link);

                        showNotification(
                          "Attendee registration link copied!",
                          "success"
                        );
                      }}
                    >
                      <i className="fas fa-copy"></i> Copy
                    </button>
                  </div>
                </div>


                {/* Speaker Registration Link */}
                <div className="dashboard-form-group">
                  <label className="dashboard-form-label">
                    Speaker Registration Link
                  </label>

                  <div className="dashboard-input-group">
                    <input
                      type="text"
                      className="dashboard-form-input"
                      value={`${window.location.origin}/speaker-form/event/${inviteEventId}`}
                      readOnly
                    />

                    <button
                      className="dashboard-btn dashboard-btn-primary"
                      onClick={() => {
                        const link = `${window.location.origin}/speaker-form/event/${inviteEventId}`;

                        navigator.clipboard.writeText(link);

                        showNotification(
                          "Speaker registration link copied!",
                          "success"
                        );
                      }}
                    >
                      <i className="fas fa-copy"></i> Copy
                    </button>
                  </div>
                </div>


                {/* Cancel */}
                <div className="dashboard-form-actions">
                  <button
                    className="dashboard-btn dashboard-btn-secondary"
                    onClick={() => setShowInviteModal(false)}
                  >
                    Cancel
                  </button>
                </div>

              </div>
            </div>
          </div>
        )}

        {/* Notifications Modal */}
        {showNotificationsModal && (
          <div
            className="dashboard-modal"
            onClick={(e) =>
              e.target.className === "dashboard-modal" &&
              setShowNotificationsModal(false)
            }
          >
            <div className="dashboard-modal-content">
              <div className="dashboard-modal-header">
                <h3>Notifications (3)</h3>
                <button
                  className="dashboard-close-modal"
                  onClick={() => setShowNotificationsModal(false)}
                >
                  &times;
                </button>
              </div>
              <div className="dashboard-modal-body">
                <div className="dashboard-notifications-list">
                  <div className="dashboard-notification-item">
                    <div className="dashboard-notification-icon dashboard-notification-success">
                      <i className="fas fa-check-circle"></i>
                    </div>
                    <div className="dashboard-notification-content">
                      <div className="dashboard-notification-title">
                        Registration Confirmed
                      </div>
                      <div className="dashboard-notification-message">
                        Your registration for Tech Summit has been confirmed
                      </div>
                      <div className="dashboard-notification-time">
                        2 hours ago
                      </div>
                    </div>
                  </div>
                  <div className="dashboard-notification-item">
                    <div className="dashboard-notification-icon dashboard-notification-warning">
                      <i className="fas fa-exclamation-circle"></i>
                    </div>
                    <div className="dashboard-notification-content">
                      <div className="dashboard-notification-title">
                        New Speaker Application
                      </div>
                      <div className="dashboard-notification-message">
                        Sarah Miller applied to speak at your event
                      </div>
                      <div className="dashboard-notification-time">1 day ago</div>
                    </div>
                  </div>
                  <div className="dashboard-notification-item">
                    <div className="dashboard-notification-icon dashboard-notification-info">
                      <i className="fas fa-calendar-check"></i>
                    </div>
                    <div className="dashboard-notification-content">
                      <div className="dashboard-notification-title">
                        Event Reminder
                      </div>
                      <div className="dashboard-notification-message">
                        Design Conference starts in 3 days
                      </div>
                      <div className="dashboard-notification-time">
                        2 days ago
                      </div>
                    </div>
                  </div>
                </div>
                <div className="dashboard-modal-footer">
                  <button
                    className="dashboard-btn dashboard-btn-secondary"
                    style={{ width: "100%" }}
                  >
                    View All Notifications
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Notification Modal */}
        {showBulkNotificationModal && (
          <div
            className="dashboard-modal"
            onClick={(e) =>
              e.target.className === "dashboard-modal" &&
              setShowBulkNotificationModal(false)
            }
          >
            <div className="dashboard-modal-content">
              <div className="dashboard-modal-header">
                <h3>Send Bulk Notification</h3>
                <button
                  className="dashboard-close-modal"
                  onClick={() => setShowBulkNotificationModal(false)}
                >
                  &times;
                </button>
              </div>
              <div className="dashboard-modal-body">
                <div className="dashboard-form-group">
                  <label className="dashboard-form-label">Recipients</label>
                  <select className="dashboard-form-input">
                    <option value="all">All Attendees (156)</option>
                    <option value="confirmed">Confirmed Only (120)</option>
                    <option value="pending">Pending Only (36)</option>
                    <option value="speakers">Speakers Only (12)</option>
                    <option value="sponsors">Sponsors Only (8)</option>
                  </select>
                </div>

                <div className="dashboard-form-group">
                  <label className="dashboard-form-label">
                    Notification Type
                  </label>
                  <select className="dashboard-form-input">
                    <option value="email">Email</option>
                    <option value="sms">SMS</option>
                    <option value="both">Email & SMS</option>
                    <option value="inapp">In-App Notification</option>
                  </select>
                </div>

                <div className="dashboard-form-group">
                  <label className="dashboard-form-label">Subject</label>
                  <input
                    type="text"
                    className="dashboard-form-input"
                    placeholder="Enter notification subject"
                  />
                </div>

                <div className="dashboard-form-group">
                  <label className="dashboard-form-label">Message</label>
                  <textarea
                    className="dashboard-form-input"
                    rows="6"
                    placeholder="Enter your message here..."
                  ></textarea>
                </div>

                <div className="dashboard-form-actions">
                  <button
                    className="dashboard-btn dashboard-btn-secondary"
                    onClick={() => setShowBulkNotificationModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="dashboard-btn dashboard-btn-primary"
                    onClick={() => {
                      showNotification(
                        "Notification sent to all selected recipients.",
                        "success",
                      );
                      setShowBulkNotificationModal(false);
                    }}
                  >
                    <i className="fas fa-paper-plane"></i> Send Notification
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Publish Confirmation Modal */}
        {showPublishModal && (
          <div
            className="dashboard-modal viewlist-modal"
            onClick={(e) =>
              e.target.className === "dashboard-modal" &&
              setShowPublishModal(false)
            }
          >
            <div
              className="dashboard-modal-content"
              style={{ maxWidth: "360px" }}
            >
              <div className="dashboard-modal-header">
                <h3>Publish this event?</h3>
                <button
                  className="dashboard-close-modal"
                  onClick={() => setShowPublishModal(false)}
                >
                  &times;
                </button>
              </div>
              <div className="dashboard-modal-body">
                <p>
                  Are you sure you want to publish this event? It will be visible
                  to the public.
                </p>
                <div
                  className="dashboard-form-actions"
                  style={{ marginTop: "20px" }}
                >
                  <button
                    className="dashboard-btn dashboard-btn-secondary"
                    onClick={() => {
                      setShowPublishModal(false);
                      setEventToPublish(null);
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="dashboard-btn dashboard-btn-primary"
                    onClick={() => {
                      setShowPublishModal(false);
                      publishEvent(eventToPublish);
                      setEventToPublish(null);
                    }}
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        
        
      </div>

    </div>
  );
};

export default UserDashboard;
